// Shopify serves the chat widget in two shapes and picks between them in the
// app embed's bundle selector, so both are supported here.
//
//   <shopify-chat>              current agent widget. Once its custom element
//                               upgrades it exposes show() / close() / open,
//                               so it is driven through that API.
//   <inbox-online-store-chat>   legacy Inbox widget. No API, so it is opened by
//                               pressing its own launcher button, which lives
//                               inside the widget's shadow DOM.
const CHAT_SELECTOR = 'shopify-chat, shopify-agent, inbox-online-store-chat';
const CHAT_TRIGGER_SELECTOR = '[data-chat-trigger]';
const CHAT_VISIBLE_CLASS = 'chat-visible';
const CHAT_PERSISTENT_CLASS = 'chat-persistent';

// Both widgets reflect their open state onto the element itself: <shopify-chat>
// carries a bare 'open' attribute only while open, the legacy widget carries
// is-open="true" / "false".
const OPEN_ATTRIBUTES = ['open', 'is-open'];

// Legacy widget only. The agent widget is opened through show() instead.
const LAUNCHER_SELECTORS = ['[data-spec="toggle-button"]', 'button.chat-toggle'];

const SCOPED_LAUNCHER_SELECTORS = [...LAUNCHER_SELECTORS, 'button'];

// The legacy chat's own close buttons, which differ between desktop and mobile.
// They sit outside the custom element, so they are only reachable through the
// composed path of a click. Matched on structure rather than aria-label, which
// is translated per locale. On mobile the class is on the icon rather than the
// button, and the composed path holds both. The agent widget needs none of this:
// every way of closing it drops the 'open' attribute, which is observed below.
const CHAT_CLOSE_SELECTORS = [
  '.chat-app--close-button',
  'button[name="closeChat"]',
  '.chat-header__close_button_mobile',
];

const chatElement = waitForElementToRender(CHAT_SELECTOR);

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

const withTimeout = (promise, timeout) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, timeout))]);

// <shopify-chat> is in the markup from the start but only gains show() / close()
// once Shopify's bundle registers the custom element, which it does in an idle
// callback after load. Resolve either way so a widget that never registers falls
// through to the launcher-button path instead of hanging the trigger.
const whenUpgraded = async (chat, timeout = 8000) => {
  if (!window.customElements) return chat;

  await withTimeout(customElements.whenDefined(chat.localName), timeout);
  customElements.upgrade?.(chat);

  return chat;
};

const hasAgentApi = (chat) => typeof chat.show === 'function';

// Open state as the widget itself reflects it. This is what OPEN_ATTRIBUTES
// observes, so it is read straight back rather than through chat.open, which
// the agent widget only syncs to the attribute afterwards.
const isChatMarkedOpen = (chat) =>
  chat.hasAttribute('open') || chat.getAttribute('is-open') === 'true';

const isChatOpen = (chat, launcher) => {
  // Agent widget: the property once upgraded, the attribute before that.
  if (typeof chat.open === 'boolean') return chat.open;
  if (chat.hasAttribute('open')) return true;

  if (launcher?.hasAttribute('aria-expanded')) {
    return launcher.getAttribute('aria-expanded') === 'true';
  }

  return chat.getAttribute('is-open') === 'true';
};

const getShadowRoot = (element) => element.shadowRoot || window.shadowRoots?.get(element) || null;

const queryDeep = (root, selector) => {
  const match = root.querySelector(selector);
  if (match) return match;

  for (const element of root.querySelectorAll('*')) {
    const nestedRoot = getShadowRoot(element);
    if (!nestedRoot) continue;

    const nested = queryDeep(nestedRoot, selector);
    if (nested) return nested;
  }

  return null;
};

const findLauncher = (chat) => {
  const shadowRoot = getShadowRoot(chat);

  if (shadowRoot) {
    for (const selector of SCOPED_LAUNCHER_SELECTORS) {
      const button = queryDeep(shadowRoot, selector);
      if (button) return button;
    }
  }

  for (const selector of SCOPED_LAUNCHER_SELECTORS) {
    const button = queryDeep(chat, selector);
    if (button) return button;
  }

  for (const selector of LAUNCHER_SELECTORS) {
    const button = queryDeep(document, selector);
    if (button) return button;
  }

  return null;
};


const getLauncher = async (chat, timeout = 8000) => {
  const deadline = Date.now() + timeout;
  let button = findLauncher(chat);

  while (!button && Date.now() < deadline) {
    await nextFrame();
    button = findLauncher(chat);
  }

  return button;
};

const pressElement = (element) => {
  const sequence = ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'];

  sequence.forEach((type) => {
    const EventConstructor =
      type.startsWith('pointer') && typeof PointerEvent === 'function' ? PointerEvent : MouseEvent;

    element.dispatchEvent(
      new EventConstructor(type, { bubbles: true, composed: true, cancelable: true })
    );
  });
};

const waitForOpen = async (chat, launcher, timeout = 1000) => {
  const deadline = Date.now() + timeout;

  while (!isChatOpen(chat, launcher) && Date.now() < deadline) {
    await nextFrame();
  }

  return isChatOpen(chat, launcher);
};

const openChat = async (chat) => {
  if (hasAgentApi(chat)) {
    if (!chat.open) chat.show();
    return true;
  }

  const launcher = await getLauncher(chat);
  if (!launcher) return false;

  if (isChatOpen(chat, launcher)) return true;

  pressElement(launcher);
  if (await waitForOpen(chat, launcher)) return true;

  pressElement(launcher);
  return waitForOpen(chat, launcher);
};

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(CHAT_TRIGGER_SELECTOR);
  if (!trigger) return;

  event.preventDefault();

  const chat = await chatElement;

  document.body.classList.add(CHAT_VISIBLE_CLASS);
  await nextFrame();

  await openChat(await whenUpgraded(chat));
});


const hideLauncher = () => {
  if (document.body.classList.contains(CHAT_PERSISTENT_CLASS)) return;
  document.body.classList.remove(CHAT_VISIBLE_CLASS);
};

document.addEventListener('click', (event) => {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

  const closed = path.some(
    (node) =>
      node?.nodeType === 1 &&
      CHAT_CLOSE_SELECTORS.some((selector) => node.matches?.(selector))
  );

  if (closed) hideLauncher();
});

chatElement.then(async (chat) => {
  new MutationObserver(() => {
    if (!isChatMarkedOpen(chat)) hideLauncher();
  }).observe(chat, { attributes: true, attributeFilter: OPEN_ATTRIBUTES });

  await whenUpgraded(chat, 15000);

  // The agent widget's own state attribute, observed above, already covers it.
  if (hasAgentApi(chat)) return;

  const launcher = await getLauncher(chat, 15000);
  if (!launcher || !launcher.hasAttribute('aria-expanded')) return;

  new MutationObserver(() => {
    if (launcher.getAttribute('aria-expanded') === 'false') hideLauncher();
  }).observe(launcher, { attributes: true, attributeFilter: ['aria-expanded'] });
});
